/**
 * CompleteStep Component
 * Final step showing success and next actions
 */

import React from 'react';
import {
  CheckCircle,
  Building2,
  User,
  ArrowRight,
  Sparkles,
  Loader2,
  BookOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { SetupProfileData, SetupEntityData } from '../types';

interface CompleteStepProps {
  profileData: Partial<SetupProfileData>;
  entityData: SetupEntityData | null;
  onComplete: () => Promise<void>;
  isSaving: boolean;
}

export const CompleteStep: React.FC<CompleteStepProps> = ({
  profileData,
  entityData,
  onComplete,
  isSaving,
}) => {
  const fullName = [profileData.firstName, profileData.lastName].filter(Boolean).join(' ');

  return (
    <div className="flex flex-col items-center text-center">
      {/* Success icon with animation */}
      <div className="relative mb-6">
        <div className="w-20 h-20 rounded-full bg-success-100 dark:bg-success-900/30 flex items-center justify-center animate-scale-in">
          <CheckCircle className="h-10 w-10 text-success-600 dark:text-success-400" />
        </div>
        <div className="absolute -top-1 -right-1">
          <Sparkles className="h-6 w-6 text-terracotta-500 animate-pulse" />
        </div>
      </div>

      <h2 className="text-2xl font-serif font-semibold text-espresso-900 dark:text-cream-100 mb-2">
        You're All Set{fullName ? `, ${profileData.firstName}` : ''}!
      </h2>

      <p className="text-espresso-500 dark:text-espresso-300 max-w-md mb-8">
        Your LegalSyncUp workspace is ready. Here's a summary of what we've configured.
      </p>

      {/* Summary cards */}
      <div className="w-full max-w-md space-y-3 mb-8">
        {/* Profile summary */}
        <div className="flex items-start gap-3 p-4 rounded-xl bg-cream-50 dark:bg-espresso-700/20 text-left">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-terracotta-100 dark:bg-terracotta-900/30 flex items-center justify-center">
            <User className="h-5 w-5 text-terracotta-600 dark:text-terracotta-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-espresso-900 dark:text-cream-100">
                Profile Configured
              </h4>
              <CheckCircle className="h-4 w-4 text-success-500" />
            </div>
            <p className="text-xs text-espresso-500 dark:text-espresso-300 truncate">
              {fullName || 'Anonymous'}
              {profileData.companyName && ` • ${profileData.companyName}`}
            </p>
          </div>
        </div>

        {/* Entity summary */}
        <div className="flex items-start gap-3 p-4 rounded-xl bg-cream-50 dark:bg-espresso-700/20 text-left">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-terracotta-100 dark:bg-terracotta-900/30 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-terracotta-600 dark:text-terracotta-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-espresso-900 dark:text-cream-100">
                {entityData ? 'First Entity Created' : 'No Entity Yet'}
              </h4>
              {entityData && <CheckCircle className="h-4 w-4 text-success-500" />}
            </div>
            <p className="text-xs text-espresso-500 dark:text-espresso-300 truncate">
              {entityData
                ? `${entityData.name} • ${entityData.uid}`
                : 'You can add entities from the Entities page'}
            </p>
          </div>
        </div>
      </div>

      {/* What's next section */}
      <div className="w-full max-w-md bg-terracotta-50 dark:bg-terracotta-900/20 rounded-xl p-4 mb-8">
        <h3 className="text-sm font-semibold text-espresso-900 dark:text-cream-100 mb-3 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-terracotta-600" />
          What's Next
        </h3>
        <ul className="text-sm text-espresso-600 dark:text-espresso-300 space-y-2 text-left">
          <li className="flex items-start gap-2">
            <ArrowRight className="h-4 w-4 text-terracotta-500 flex-shrink-0 mt-0.5" />
            <span>
              {entityData
                ? 'Add documents and start workflows for your entity'
                : 'Register your first Swiss entity from the Entities page'}
            </span>
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="h-4 w-4 text-terracotta-500 flex-shrink-0 mt-0.5" />
            <span>Invite team members to collaborate on compliance</span>
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="h-4 w-4 text-terracotta-500 flex-shrink-0 mt-0.5" />
            <span>Explore the Dashboard for a quick overview</span>
          </li>
        </ul>
      </div>

      {/* CTA buttons */}
      <div className="flex flex-col sm:flex-row items-center gap-3 w-full max-w-md">
        <Button
          onClick={onComplete}
          disabled={isSaving}
          className="w-full sm:w-auto bg-terracotta-600 hover:bg-terracotta-700 text-white px-8"
          size="lg"
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Finishing...
            </>
          ) : (
            <>
              Go to Dashboard
              <ArrowRight className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </div>

      {/* Help link */}
      <p className="text-xs text-espresso-400 dark:text-espresso-300 mt-4">
        Need help?{' '}
        <a
          href="/docs"
          className="text-terracotta-600 hover:text-terracotta-700 underline"
        >
          Check out our documentation
        </a>
      </p>
    </div>
  );
};

export default CompleteStep;
